import React from 'react';
import {
  Package, Zap, ShoppingCart, Building2, Hammer, Wheat, Factory, Briefcase,
  Calendar, CreditCard, Wallet, User, BarChart3, Mail, UserCheck,
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../ui/tooltip';

/* -------------------------------------------------------------------------- */
/*                             Helpers (palette)                              */
/* -------------------------------------------------------------------------- */

const initialsOf = (name = '') => {
  const cleaned = name.trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const sectorIconFor = (sectorCode = '', sectorName = '') => {
  const key = `${sectorCode} ${sectorName}`.toUpperCase();
  if (/TRANSPORT|LOGIST/.test(key)) return <Package className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/ENERG|ELECTRIC|UTILIT/.test(key)) return <Zap className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/DISTRIB|COMMERCE|RETAIL|SUPERMARC|GROSSIST/.test(key)) return <ShoppingCart className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/BTP|CONSTRUCT|TRAVAUX|IMMOB/.test(key)) return <Hammer className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/AGRI|AGRO|ALIMENT|FOOD/.test(key)) return <Wheat className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/INDUST|MANUF|USINE/.test(key)) return <Factory className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  if (/SERVICE|CONSEIL|FINANCE|TECH/.test(key)) return <Briefcase className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
  return <Building2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />;
};

/* -------------------------------------------------------------------------- */
/*                              Company Avatar                                */
/* -------------------------------------------------------------------------- */

export const CompanyAvatar = ({ name }) => (
  <div
    className="w-10 h-10 shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm"
    aria-hidden
  >
    {initialsOf(name)}
  </div>
);

/* -------------------------------------------------------------------------- */
/*                          Client column (name+meta)                         */
/* -------------------------------------------------------------------------- */

export const ClientCell = ({ company, onOpen }) => {
  const sector = company.sectorName;
  const email = company.billingEmail || company.billingEmailFallback;
  return (
    <div className="flex items-start gap-3 min-w-[220px]">
      <CompanyAvatar name={company.companyName} />
      <div className="space-y-0.5 min-w-0">
        <button
          onClick={onOpen}
          className="text-left font-semibold text-slate-900 hover:text-emerald-700 transition-colors text-sm truncate block"
          data-testid={`open-profile-${company.companyName}`}
        >
          {company.companyName}
        </button>
        {sector && (
          <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{sector}</span>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
            <Mail className="w-3 h-3 shrink-0" />
            <span className="truncate">{email}</span>
          </div>
        )}
        {company.tvaStatus === 'assujetti' && (
          <Badge variant="outline" className="mt-1 h-4 px-1.5 text-[9px] border-slate-200 text-slate-600 bg-slate-50 font-normal">
            Assujetti TVA
          </Badge>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                            Completeness donut                              */
/* -------------------------------------------------------------------------- */

const FIELDS = [
  { key: 'ncc', label: 'NCC' },
  { key: 'rccm', label: 'RCCM' },
  { key: 'tvaStatus', label: 'Statut TVA' },
  { key: 'email', label: 'Email facturation' },
  { key: 'sector', label: 'Secteur' },
  { key: 'address', label: 'Adresse' },
  { key: 'policy', label: 'Modalité paiement' },
  { key: 'fne', label: 'FNE activé' },
];

const fieldFilledForCompany = (company) => ({
  ncc: !!company.ncc,
  rccm: !!company.rccm,
  tvaStatus: !!company.tvaStatus,
  email: !!(company.billingEmail || company.billingEmailFallback),
  sector: !!company.sectorCode,
  address: !!company.address,
  policy: !!company.defaultPaymentPolicyId,
  fne: !!company.fneBillingEnabled,
});

export const CompletenessBlock = ({ company, onClickDetails }) => {
  const filledMap = fieldFilledForCompany(company);
  const filled = FIELDS.filter((f) => filledMap[f.key]).length;
  const total = FIELDS.length;
  const pct = (filled / total) * 100;
  const missing = FIELDS.filter((f) => !filledMap[f.key]).map((f) => f.label);

  // Color tier
  let tone = 'rose'; // < 5
  if (filled >= 8) tone = 'emerald';
  else if (filled >= 5) tone = 'amber';

  const palette = {
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-100',    text: 'text-rose-700',    ring: 'stroke-rose-500',    label: 'Profil incomplet' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-100',   text: 'text-amber-700',   ring: 'stroke-amber-500',   label: 'Profil incomplet' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', ring: 'stroke-emerald-500', label: 'Profil complet' },
  }[tone];

  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClickDetails}
            className={`w-full max-w-[120px] rounded-lg ${palette.bg} ${palette.border} border px-3 py-2 flex flex-col items-center gap-1 hover:brightness-95 transition-all`}
            data-testid={`completeness-${company.companyName}`}
          >
            <div className="flex items-center gap-2">
              <svg width="32" height="32" viewBox="0 0 36 36" className="-rotate-90">
                <circle cx="18" cy="18" r={radius} fill="none" className="stroke-slate-200" strokeWidth="3.5" />
                <circle
                  cx="18" cy="18" r={radius} fill="none"
                  className={palette.ring}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                />
              </svg>
              <span className={`text-base font-semibold ${palette.text}`}>{filled}/{total}</span>
            </div>
            <span className={`text-[11px] font-medium ${palette.text}`}>{palette.label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 text-slate-50 text-xs p-3 max-w-xs">
          <div className="font-semibold mb-1">Complétude {filled}/{total}</div>
          {missing.length === 0 ? (
            <div className="text-emerald-300">Tous les champs sont renseignés.</div>
          ) : (
            <>
              <div className="text-slate-300 mb-1">Champs manquants :</div>
              <ul className="space-y-0.5">
                {missing.map((m) => (
                  <li key={m} className="text-rose-300">• {m}</li>
                ))}
              </ul>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/* -------------------------------------------------------------------------- */
/*                            Fiscality badges                                */
/* -------------------------------------------------------------------------- */

const FiscBadge = ({ ok, missing, neutral, label }) => {
  let cls = 'bg-rose-50 text-rose-700 border-rose-100';
  if (ok) cls = 'bg-emerald-50 text-emerald-700 border-emerald-100';
  else if (neutral) cls = 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <span
      className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${cls}`}
    >
      {label || (ok ? 'Renseigné' : missing)}
    </span>
  );
};

export const FiscalityCell = ({ company, onClickDetails }) => {
  const hasTva = !!company.tvaStatus;
  const tvaAssujetti = company.tvaStatus === 'assujetti';
  return (
    <div className="space-y-1 min-w-[150px]">
      <FiscBadge
        ok={!!company.ncc}
        label={company.ncc ? 'NCC renseigné' : 'NCC manquant'}
      />
      <div className="block" />
      <FiscBadge
        ok={!!company.rccm}
        label={company.rccm ? 'RCCM renseigné' : 'RCCM manquant'}
      />
      <div className="block" />
      <FiscBadge
        ok={tvaAssujetti}
        neutral={hasTva && !tvaAssujetti}
        label={
          tvaAssujetti
            ? 'TVA assujetti'
            : hasTva
              ? 'TVA non assujetti'
              : 'TVA manquante'
        }
      />
      <button
        onClick={onClickDetails}
        className="block text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline mt-1"
        data-testid={`fiscality-details-${company.companyName}`}
      >
        Voir les détails
      </button>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Payment card                                  */
/* -------------------------------------------------------------------------- */

const paymentIconFor = (policyName = '') => {
  const key = policyName.toUpperCase();
  if (/COMPTANT|AVANT|100%/.test(key)) return <CreditCard className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />;
  if (/30|60|JOURS|DELAI|TERME/.test(key)) return <Calendar className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />;
  return <Wallet className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />;
};

export const PaymentCell = ({ company }) => {
  const name = company.defaultPaymentPolicyName;
  if (!name) {
    return (
      <div className="min-w-[140px] text-xs text-slate-400 italic">Non renseigné</div>
    );
  }
  const icon = paymentIconFor(name);
  // Try to break long policy names: "Comptant — 100% avant mise à disposition"
  const parts = name.split(/—|-|–/).map((s) => s.trim()).filter(Boolean);
  const title = parts[0] || name;
  const subtitle = parts.slice(1).join(' — ');
  return (
    <div className="min-w-[140px] max-w-[180px] inline-flex items-start gap-2 bg-amber-50/60 border border-amber-100 rounded-lg px-2.5 py-2">
      {icon}
      <div className="text-xs text-slate-700 leading-tight">
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Activity card                                 */
/* -------------------------------------------------------------------------- */

export const ActivityCell = ({ company }) => {
  if (!company.sectorName) {
    return <div className="min-w-[140px] text-xs text-slate-400 italic">Non renseigné</div>;
  }
  return (
    <div className="min-w-[140px] max-w-[180px] inline-flex items-start gap-2 bg-emerald-50/60 border border-emerald-100 rounded-lg px-2.5 py-2">
      {sectorIconFor(company.sectorCode, company.sectorName)}
      <div className="text-xs text-slate-700 leading-tight font-medium">
        {company.sectorName}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Relation cell                                 */
/* -------------------------------------------------------------------------- */

export const RelationCell = ({ company }) => (
  <div className="text-xs text-slate-600 space-y-1.5 min-w-[110px]">
    <div className="flex items-center gap-1.5">
      <User className="w-3.5 h-3.5 text-emerald-600" />
      <span className="font-medium text-slate-800">{company.memberCount || 0}</span>
      <span className="text-slate-500">{(company.memberCount || 0) > 1 ? 'membres' : 'membre'}</span>
    </div>
    <div className="flex items-center gap-1.5">
      <BarChart3 className="w-3.5 h-3.5 text-emerald-600" />
      <span className="font-medium text-slate-800">{company.operationsCount || 0}</span>
      <span className="text-slate-500">{(company.operationsCount || 0) > 1 ? 'opérations' : 'opération'}</span>
    </div>
    {company.assignedCommercialName ? (
      <div className="flex items-center gap-1.5 mt-0.5 pt-1 border-t border-slate-100" title="Commercial assigné">
        <UserCheck className="w-3.5 h-3.5 text-blue-600" />
        <span className="text-[11px] text-slate-700 truncate max-w-[140px]" title={company.assignedCommercialName}>
          {company.assignedCommercialName}
        </span>
      </div>
    ) : (
      <div className="flex items-center gap-1.5 mt-0.5 pt-1 border-t border-slate-100" title="Aucun commercial assigné">
        <UserCheck className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-[10px] text-slate-400 italic">Sans commercial</span>
      </div>
    )}
  </div>
);
